import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { aiCodingJobQueries } from '../../hooks/backend/aiCodingJobs';
import {
  useCreateResponseAICoding,
  useDeleteResponseAICoding,
} from '../../hooks/backend/responseAICodings';
import { QuestionCategory } from '../../services/backend/questionCategories';
import {
  classifyResponses,
  CompletedQuestionResponse,
} from '../../services/backend/responseAICodings';
import {
  generateCategoryCode,
  getCategoryBadgeColor,
} from '../../util/aiCodingCategories';
import { downloadCSV } from '../../util/files';
import Badge from '../common/Badge';
import CircleLoader from '../common/CircleLoader';
import ErrorDisplay from '../common/ErrorDisplay';
import Modal, { ModalHeader } from '../common/Modal';
import Button from '../common/forms/Button';
import Icon from '../common/Icon';
import IconBackground from '../common/icons/IconBackground';
import Listbox, { ListboxButton, ListboxOption } from '../common/Listbox';
import Tooltip from '../common/Tooltip';

interface AICodingClassificationTabProps {
  questionId: number;
  categories: QuestionCategory[];
  classificationResults: CompletedQuestionResponse[] | undefined;
  isLoadingClassification: boolean;
  classificationError: Error | null;
  isClassificationProcessing: boolean;
  selectedWaveId: number | null | undefined;
  onError: (error: string | null) => void;
}

const AICodingClassificationTab = ({
  questionId,
  categories,
  classificationResults,
  isLoadingClassification,
  classificationError,
  isClassificationProcessing,
  selectedWaveId,
  onError,
}: AICodingClassificationTabProps): JSX.Element => {
  const queryClient = useQueryClient();

  const [addingCodingForRespondent, setAddingCodingForRespondent] = useState<
    number | null
  >(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  const [deletingCategoryId, setDeletingCategoryId] = useState<number | null>(
    null,
  );
  const [confirmDeleteModal, setConfirmDeleteModal] = useState<{
    responseAICodingId: number;
    categoryName: string;
  } | null>(null);

  const classifiedResponses = useMemo(() => {
    return classificationResults || [];
  }, [classificationResults]);

  const createCodingMutation = useCreateResponseAICoding({
    onSuccess: () => {
      setAddingCodingForRespondent(null);
      setSelectedCategoryId(null);
      onError(null);
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: { service: 'responseAICodings', operation: 'create' },
        extra: { questionId, selectedCategoryId, addingCodingForRespondent },
      });
      onError('Failed to add classification');
    },
  });

  const deleteCodingMutation = useDeleteResponseAICoding({
    onSuccess: () => {
      setDeletingCategoryId(null);
      onError(null);
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: { service: 'responseAICodings', operation: 'delete' },
        extra: { questionId, deletingCategoryId },
      });
      setDeletingCategoryId(null);
      onError('Failed to remove classification');
    },
  });

  const handleDeleteCategory = (
    responseAICodingId: number,
    categoryName: string,
  ) => {
    setConfirmDeleteModal({ responseAICodingId, categoryName });
  };

  const confirmDelete = () => {
    if (confirmDeleteModal) {
      setDeletingCategoryId(confirmDeleteModal.responseAICodingId);
      deleteCodingMutation.mutate({
        id: confirmDeleteModal.responseAICodingId,
      });
      setConfirmDeleteModal(null);
    }
  };

  const cancelDelete = () => {
    setConfirmDeleteModal(null);
  };

  const handleClassify = async () => {
    try {
      onError(null);
      await classifyResponses({
        questionId,
        waveId: selectedWaveId ?? undefined,
      });
      await queryClient.invalidateQueries(
        aiCodingJobQueries.byQuestion({ questionId }),
      );
    } catch {
      onError('Failed to start classification');
    }
  };

  const handleAddCoding = () => {
    onError(null);

    if (!selectedCategoryId || !addingCodingForRespondent) {
      onError('Please select a category');
      return;
    }

    const currentResponse = classifiedResponses.find(
      (r) => r.respondentId === addingCodingForRespondent,
    );

    const isDuplicate = currentResponse?.responseCodingCategories?.some(
      (cat) => cat.id === selectedCategoryId,
    );

    if (isDuplicate) {
      onError('This category is already assigned to this response');
      return;
    }

    createCodingMutation.mutate({
      questionCategoryId: selectedCategoryId,
      respondentId: addingCodingForRespondent,
    });
  };

  const startAddingCoding = (respondentId: number) => {
    onError(null);
    setAddingCodingForRespondent(respondentId);
    setSelectedCategoryId(null);
  };

  const cancelAddingCoding = () => {
    onError(null);
    setAddingCodingForRespondent(null);
    setSelectedCategoryId(null);
  };

  const downloadClassificationCSV = () => {
    const data = classifiedResponses.map((r) => ({
      'Respondent ID': r.respondentId,
      Response: r.response,
      Categories: (r.responseCodingCategories || [])
        .map((cat) => cat.name)
        .join('; '),
    }));
    downloadCSV({
      data,
      filename: `classification_${questionId}.csv`,
      errorLabel: 'classification',
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-start gap-4 mb-6">
        <Button
          disabled={isClassificationProcessing}
          hierarchy="primary"
          icon={
            isClassificationProcessing ? (
              <CircleLoader />
            ) : (
              <Icon id="lightning-01" />
            )
          }
          iconPlacement="leading"
          onClick={handleClassify}
          size="md"
        >
          {isClassificationProcessing ? 'Classifying...' : 'Classify'}
        </Button>

        <div className="ml-auto">
          <Button
            disabled={classifiedResponses.length === 0}
            hierarchy="secondary-gray"
            icon={<Icon id="download" />}
            onClick={downloadClassificationCSV}
            size="md"
          >
            Download CSV
          </Button>
        </div>
      </div>

      <div className="mb-4 text-xs text-gray-d-500 leading-relaxed">
        Glass cannot guarantee the accuracy of any results generated via AI.
        Please be sure to validate any automatically generated results.
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="border border-gray-d-200 rounded-lg bg-white">
          {isLoadingClassification ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-d-500">Loading classifications...</div>
            </div>
          ) : classificationError ? (
            <div className="flex items-center justify-center py-12 px-6">
              <ErrorDisplay message="Failed to load classifications" />
            </div>
          ) : classifiedResponses.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-d-500">No classifications found</div>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[400px]">
              <table className="w-full">
                <thead className="bg-gray-d-50 sticky top-0 z-10 border-b border-gray-d-200">
                  <tr>
                    <th className="py-3 px-6 text-left text-sm font-semibold text-gray-d-600 w-1/4">
                      Categories
                    </th>
                    <th className="py-3 px-6 text-left text-sm font-semibold text-gray-d-600">
                      Response
                    </th>
                    <th className="py-3 px-6 text-center text-sm font-semibold text-gray-d-600 w-24">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {classifiedResponses.map((responseItem, index) => (
                    <tr
                      key={responseItem.respondentId}
                      className={clsx(
                        'hover:bg-gray-d-200 transition-colors border-b border-gray-d-100 last:border-0',
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-d-50',
                        addingCodingForRespondent ===
                          responseItem.respondentId && 'bg-blue-50',
                      )}
                    >
                      <td className="py-4 px-6 align-top">
                        <div className="flex flex-wrap gap-2 mb-2">
                          {responseItem.responseCodingCategories &&
                          responseItem.responseCodingCategories.length > 0 ? (
                            responseItem.responseCodingCategories.map(
                              (categoryObj) => {
                                const badgeProps = getCategoryBadgeColor(
                                  categoryObj.name,
                                  categories,
                                );

                                return (
                                  <div
                                    key={categoryObj.responseAICodingId}
                                    className="flex items-center gap-1"
                                  >
                                    <Tooltip
                                      trigger={
                                        <Badge {...badgeProps} size="sm">
                                          {generateCategoryCode(
                                            categoryObj.name,
                                          )}
                                        </Badge>
                                      }
                                    >
                                      {categoryObj.name}
                                    </Tooltip>
                                    <IconBackground
                                      disabled={
                                        deleteCodingMutation.isPending ||
                                        createCodingMutation.isPending ||
                                        deletingCategoryId ===
                                          categoryObj.responseAICodingId
                                      }
                                      onClick={() =>
                                        handleDeleteCategory(
                                          categoryObj.responseAICodingId,
                                          categoryObj.name,
                                        )
                                      }
                                      size="small"
                                      title="Remove category"
                                    >
                                      {deletingCategoryId ===
                                      categoryObj.responseAICodingId ? (
                                        <CircleLoader />
                                      ) : (
                                        <div className="w-3 h-3 text-red-600">
                                          <Icon id="x" />
                                        </div>
                                      )}
                                    </IconBackground>
                                  </div>
                                );
                              },
                            )
                          ) : (
                            <span className="text-gray-d-400 italic text-sm">
                              Uncategorized
                            </span>
                          )}
                        </div>

                        {addingCodingForRespondent ===
                          responseItem.respondentId && (
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex-1">
                              <Listbox
                                button={
                                  <ListboxButton className="w-full rounded-lg border border-gray-d-300 shadow-sm text-gray-d-900 outline-primary-d-600 py-2 px-3 text-sm flex justify-between items-center bg-white">
                                    <span className="block truncate">
                                      {selectedCategoryId
                                        ? categories.find(
                                            (c) => c.id === selectedCategoryId,
                                          )?.name || 'Select category'
                                        : 'Select category'}
                                    </span>
                                    <div className="w-4 h-4 text-gray-d-500">
                                      <Icon id="chevron-down" />
                                    </div>
                                  </ListboxButton>
                                }
                                onChange={setSelectedCategoryId}
                                value={selectedCategoryId}
                              >
                                {categories
                                  .filter(
                                    (category) =>
                                      !responseItem.responseCodingCategories?.some(
                                        (codingCategory) =>
                                          codingCategory.id === category.id,
                                      ),
                                  )
                                  .map((category) => (
                                    <ListboxOption
                                      key={category.id}
                                      value={category.id}
                                    >
                                      <div className="flex items-center gap-2">
                                        <Badge
                                          {...getCategoryBadgeColor(
                                            category.name,
                                            categories,
                                          )}
                                          size="sm"
                                        >
                                          {generateCategoryCode(category.name)}
                                        </Badge>
                                        <span>{category.name}</span>
                                      </div>
                                    </ListboxOption>
                                  ))}
                              </Listbox>
                            </div>
                            <IconBackground
                              disabled={createCodingMutation.isPending}
                              onClick={handleAddCoding}
                              title="Add category"
                            >
                              {createCodingMutation.isPending ? (
                                <CircleLoader />
                              ) : (
                                <div className="w-4 h-4 text-green-600">
                                  <Icon id="check" />
                                </div>
                              )}
                            </IconBackground>
                            <IconBackground
                              onClick={cancelAddingCoding}
                              title="Cancel"
                            >
                              <div className="w-4 h-4 text-gray-600">
                                <Icon id="x" />
                              </div>
                            </IconBackground>
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-6 align-top">
                        <div className="text-sm text-gray-d-900">
                          {responseItem.response}
                        </div>
                      </td>
                      <td className="py-4 px-6 align-top">
                        <div className="flex items-center justify-center">
                          {addingCodingForRespondent !==
                            responseItem.respondentId && (
                            <IconBackground
                              disabled={
                                categories.length === 0 ||
                                createCodingMutation.isPending ||
                                deleteCodingMutation.isPending
                              }
                              onClick={() =>
                                startAddingCoding(responseItem.respondentId)
                              }
                              title="Add category"
                            >
                              <div className="w-4 h-4 text-primary-600">
                                <Icon id="plus" />
                              </div>
                            </IconBackground>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {confirmDeleteModal && (
        <Modal
          header={
            <ModalHeader onClickClose={cancelDelete}>
              Confirm Removal
            </ModalHeader>
          }
          onCloseModal={cancelDelete}
          position="center"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-d-700">
              Are you sure you want to remove the "
              {confirmDeleteModal.categoryName}" category from this response?
            </p>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                disabled={deleteCodingMutation.isPending}
                hierarchy="destructive"
                onClick={confirmDelete}
                size="md"
              >
                Remove Category
              </Button>
              <Button
                hierarchy="secondary-gray"
                onClick={cancelDelete}
                size="md"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AICodingClassificationTab;
